import { UserFilter } from "@coaching-culture/types";
import { Box, Button, EmptyState, Loader, Text } from "@coaching-culture/ui";
import { useOrg } from "auth/OrgProvider";
import CenterColumn from "components/CenterColumn";
import { FilterModal } from "components/FilterPanel";
import { PageHeader } from "components/PageHeader";
import { sortBy, uniqBy } from "lodash";
import { useAvailableContent } from "queries/mindset";
import { useMemo, useState } from "react";
import { FaBook } from "react-icons/fa";
import { ContentReport } from "./ContentReport";
import { ContentSummaryCharts } from "./ContentSummaryCharts";
import { MatrixReport } from "./MatrixReport";
import { SummaryReport } from "./SummaryReport";

const LessonsReport = () => {
  const content = useAvailableContent();
  const [org] = useOrg();
  const loading = content.isLoading;
  const [showFilters, setShowFilters] = useState(false);
  const [selectedLesson, setSelectedLesson] = useState<string | null>("all");
  const [filter, setFilter] = useState<UserFilter>({
    groups: [],
    userFields: [],
  });

  const lessons = useMemo(
    () => (content.data ?? []).filter((x) => x.type === "lesson"),
    [content.data],
  );

  const noData = content.isFetched && lessons.length === 0;

  const isMatrix =
    selectedLesson != null &&
    (selectedLesson.startsWith("cat") || selectedLesson === "all");

  const selectedCat = selectedLesson?.startsWith("cat-")
    ? selectedLesson.replace("cat-", "")
    : "";

  const catLessons = useMemo(
    () =>
      sortBy(
        lessons.filter(
          (x) => selectedLesson === "all" || x.category.id === selectedCat,
        ),
        (x) => [x.category.id, x.orderIndex],
      ),
    [selectedCat, selectedLesson, lessons],
  );

  const cats = uniqBy(
    lessons.map((x) => x.category),
    "id",
  );

  const lessonOptions = [
    { value: "summary", label: "Summary By Lesson" },
    { value: "all", label: "All Lessons" },
    ...sortBy(cats, "orderIndex").flatMap((x) => [
      { label: `${x.name} (Summary)`, value: `cat-${x.id}` },
      ...sortBy(
        lessons.filter((l) => l.category.id === x.id),
        "orderIndex",
      ).map((l) => ({
        label: `${x.name} / ${l.name}`,
        value: `les-${l.id}`,
      })),
    ]),
  ];

  if (noData) {
    return (
      <CenterColumn>
        <PageHeader
          text="Insights"
          subtitle="An overview of the data collected by the platform"
        />
        <EmptyState icon={FaBook}>No topics available.</EmptyState>
      </CenterColumn>
    );
  }

  const commonProps = {
    options: lessonOptions,
    selected: selectedLesson,
    filter,
    type: "lesson",
    onChange: setSelectedLesson,
    onFilterChange: setFilter,
  } as const;

  return (
    <>
      <FilterModal
        isOpen={showFilters}
        filter={filter}
        onClose={(result) => {
          if (result != null) {
            setFilter(result);
          }
          setShowFilters(false);
        }}
      />
      <CenterColumn>
        <PageHeader
          text="Insights"
          subtitle="An overview of the data collected by the platform"
        >
          <Button
            ml={[0, 2]}
            color="primary"
            onClick={() => setShowFilters(true)}
          >
            Filters ({filter.groups.length + filter.userFields.length})
          </Button>
        </PageHeader>
        {org != null && !org.hideContentInsightsSummary && (
          <Box mb={3}>
            <Text mb={3} fontSize="section" fontWeight={600} color="black">
              Summary
            </Text>
            <ContentSummaryCharts type="lesson" filter={filter} />
          </Box>
        )}
        <Box>
          <Text mb={3} fontSize="section" fontWeight={600} color="black">
            Lessons
          </Text>
          {selectedLesson == null ? (
            <Loader />
          ) : (
            <>
              {loading ? (
                <Loader />
              ) : selectedLesson === "summary" ? (
                <SummaryReport {...commonProps} />
              ) : isMatrix ? (
                <MatrixReport {...commonProps} content={catLessons} />
              ) : (
                <ContentReport
                  {...commonProps}
                  contentId={selectedLesson.replace("les-", "")}
                />
              )}
            </>
          )}
        </Box>
      </CenterColumn>
    </>
  );
};

export default LessonsReport;
